// src/components/ClientSiteDashboard.jsx
//
// Client-facing equivalent of the admin Site Dashboard. Reuses the exact
// same fleet-table/mobile-card design and the exact same DeviceRow,
// MGMT_LABEL, SITE_TYPE_LABEL and offline-duration helpers as
// SiteDashboard.jsx (imported directly, not duplicated) - so this page
// and the admin one always render devices identically. Only the data
// scope and a handful of admin-only, fleet-wide features differ:
//
//   - Data source: /api/v2/client/dashboard (this client's own linked
//     sites only) instead of the paginated /api/v2/admin/sites-page.
//   - No fleet-wide stat cards (Peplink Online / Starlink Lines Online)
//     - those come from /api/v2/admin/dashboard's summary, which is a
//     whole-company aggregate a client has no business seeing.
//   - No global device search, no "Unassigned" devices section, no
//     Live Event Feed sidebar - all three are admin/fleet-wide concepts
//     that don't have a client-scoped equivalent.
//   - Sites auto-expand when the client only has 1-2 of them, so they
//     don't need an extra click to see what's on them - not something
//     the admin list does, since it can have dozens of sites.
//
// Role gating (confirmed against the real ClientDashboard.jsx /
// Modal.jsx logic - both canMarkImportant and canChangePriority are
// gated by the exact same check: userRole === 'admin'):
//   - client admin: full parity with internal admin - can change WAN
//     priority and mark important, same as the admin Site Dashboard.
//   - operator/viewer: same data, same usage/telemetry, read-only -
//     LiveWanPanel's canEdit prop is false, which swaps the interactive
//     ImportanceToggle for a plain read-only star and hides the
//     "Change Priority" button entirely. LiveWanPanel is additionally
//     given scope="client" (the admin page never passes this) so it
//     stays inside client-permitted actions even for a client admin -
//     e.g. no access to internal-only fields/actions the WAN panel
//     exposes for staff.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';
import LiveWanPanel from './LiveWanPanel';
import {
  DeviceRow,
  SITE_TYPE_LABEL,
  isPendingRoam,
  getSiteDownSince,
  formatDownDuration,
} from './SiteDashboard';
import './SiteDashboard.css';
import '../styles/fleet-theme.css';
import { can } from '../utils/permissions';
const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4'; // lives in public/, not src/images/ - needed for a stable, non-hashed URL the index.html preload tag can target

export default function ClientSiteDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sites, setSites] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [expandedDevice, setExpandedDevice] = useState(null); // device_key of the open live panel, or null
  const [expandedMode, setExpandedMode] = useState('wan'); // 'wan' | 'clients' - which section to show when opened
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeFilter, setActiveFilter] = useState(null); // null | 'starlink_any' | 'sites_down'
  const [sortMode, setSortMode] = useState('attention_first'); // 'attention_first' | 'default'

  // Matches legacy Dashboard.jsx -> Modal.jsx: canMarkImportant and
  // canChangePriority are both gated by userRole === 'admin'. Same check
  // the admin SiteDashboard.jsx applies.
  // Settings > Roles: "Control devices" (server checks devices.control too)
  const canEdit = can(user, 'devices.control');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await apiFetchJSON('/api/v2/client/dashboard?grouped=true');
      const loadedSites = json.sites || [];
      setSites(loadedSites);
      // Sites with only one or two devices shouldn't need an extra
      // click to see what's on them - the admin list never does this
      // (it can have dozens of sites), but a client's own list is
      // small by nature.
      const autoExpand = {};
      loadedSites.forEach((s) => { autoExpand[s.site_id] = loadedSites.length <= 2; });
      setExpanded(autoExpand);
    } catch (e) {
      setError(e.message || 'Failed to load your sites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (siteId) =>
    setExpanded((prev) => ({ ...prev, [siteId]: !prev[siteId] }));

  // Same rules as the admin dashboard: a site is "fully down" once its
  // LAST still-online device drops (Peplink + Starlink together).
  const isSiteDown = (s) => s.devices.length > 0 && s.devices.every((d) => d.online === false);

  const sitesDownCount = sites.filter(isSiteDown).length;
  const attentionSitesCount = sites.filter((s) =>
    s.devices.some((d) => d.online === false && !isPendingRoam(d))
  ).length;
  const starlinkSitesCount = sites.filter((s) => s.devices.some((d) => d.device_type === 'starlink')).length;

  // No search or stat-card filters on this page - activeFilter can only
  // ever be set by the pill row below, so this only needs to cover the
  // two filters those pills offer.
  const matchesFilter = (d, site) => {
    if (!activeFilter) return true;
    if (activeFilter === 'sites_down') return isSiteDown(site);
    if (activeFilter === 'starlink_any') return d.device_type === 'starlink';
    return true;
  };

  let visibleSites = sites
    .map((s) => ({ ...s, devices: s.devices.filter((d) => matchesFilter(d, s)) }))
    .filter((s) => (!activeFilter ? true : s.devices.length > 0));

  if (sortMode === 'attention_first') {
    // Sites with at least one offline device first, otherwise preserve
    // original order within each group (stable sort).
    visibleSites = visibleSites
      .map((s, i) => ({ s, i, hasOffline: s.devices.some((d) => d.online === false && !isPendingRoam(d)) }))
      .sort((a, b) => (b.hasOffline - a.hasOffline) || (a.i - b.i))
      .map((x) => x.s);
  }

  // Generic row click (name/caret/badge area) - goes straight to the
  // historical monitoring page, matching what was originally agreed.
  const handleRowClick = (d) => {
    if (d.device_type === 'peplink') {
      navigate(`/monitoring/${d.device_key}`);
    } else if (d.device_type === 'starlink') {
      if (d.service === 'roam' || d.service === 'missing') return; // no telemetry for ROAM; MISSING has left ELCOME
      navigate(`/starlink/${d.device_key}`);
    }
  };

  // WAN/Client stat clicks still open the live panel - this is still the
  // only place WAN priority/mark-important editing happens (monitoring
  // is deliberately read-only), so this path has to stay.
  const handleStatClick = (d, mode) => {
    if (d.device_type !== 'peplink') return;
    if (expandedDevice === d.device_key) {
      if (expandedMode === mode) {
        setExpandedDevice(null);
      } else {
        setExpandedMode(mode);
      }
    } else {
      setExpandedDevice(d.device_key);
      setExpandedMode(mode);
    }
  };

  // ---- Guided tour ("?" in FleetHeader) ---------------------------------
  // Same 'site-dashboard' tour as the admin page; FleetHeader's
  // scope="client" switches it to the client wording/steps.
  const tourSite =
    visibleSites.find((s) => s.devices.some((d) => d.device_type === 'peplink' && d.online)) ||
    visibleSites.find((s) => s.devices.some((d) => d.device_type === 'peplink')) ||
    visibleSites.find((s) => s.devices.length > 0) ||
    visibleSites[0] || null;
  const tourDevice = tourSite
    ? (tourSite.devices.find((d) => d.device_type === 'peplink' && d.online) ||
       tourSite.devices.find((d) => d.device_type === 'peplink') || tourSite.devices[0] || null)
    : null;
  const tourSnapshotRef = useRef(null);
  usePageTour('site-dashboard', {
    isMobile,
    siteCount: sites.length,
    sitesDownCount,
    attentionSitesCount,
    starlinkSitesCount,
    tourSite,
    tourDevice,
    canEdit,
    expandTourSite: () => tourSite && setExpanded((prev) => ({ ...prev, [tourSite.site_id]: true })),
    onTourStart: () => {
      tourSnapshotRef.current = { expanded, expandedDevice };
      setExpandedDevice(null);
    },
    onTourEnd: () => {
      if (!tourSnapshotRef.current) return;
      setExpanded(tourSnapshotRef.current.expanded);
      setExpandedDevice(tourSnapshotRef.current.expandedDevice);
    },
  }, { ready: !loading && !error });

  // Early returns live below every hook (React rules of hooks).
  if (loading) {
    return (
      <div className="cm-loading-overlay">
        <div className="cm-spinner-container">
          <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
            <source src={loadingVideo} type="video/mp4" />
          </video>
          <p>Loading your devices…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sd-page">
        <FleetHeader title="Site Dashboard" user={user} onLogout={onLogout} scope="client" />
        <div className="sd-wrap">
          <div className="ft-toolbar-error" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sd-page">
      <FleetHeader title="Site Dashboard" user={user} onLogout={onLogout} scope="client" />

      <div className="sd-wrap">
        <div className="ft-filter-row" data-tour="sd-pills">
          {isMobile ? (
            <>
              <button
                className={'ft-pill' + (sortMode === 'attention_first' ? ' active' : '')}
                onClick={() => setSortMode('attention_first')}
              >
                ATTENTION {attentionSitesCount}
              </button>
              <button
                className={'ft-pill' + (activeFilter === 'sites_down' ? ' active' : '')}
                onClick={() => setActiveFilter((prev) => (prev === 'sites_down' ? null : 'sites_down'))}
              >
                DOWN {sitesDownCount}
              </button>
              <button
                className={'ft-pill' + (sortMode === 'default' && !activeFilter ? ' active' : '')}
                onClick={() => { setSortMode('default'); setActiveFilter(null); }}
              >
                ALL {sites.length}
              </button>
            </>
          ) : (
            <>
              <button
                className={'ft-pill' + (sortMode === 'attention_first' ? ' active' : '')}
                onClick={() => setSortMode('attention_first')}
              >
                ATTENTION · {attentionSitesCount}
              </button>
              <button
                className={'ft-pill' + (sortMode === 'default' && !activeFilter ? ' active' : '')}
                onClick={() => { setSortMode('default'); setActiveFilter(null); }}
              >
                ALL · {sites.length}
              </button>
              <button
                className={'ft-pill' + (activeFilter === 'starlink_any' ? ' active' : '')}
                onClick={() => setActiveFilter((prev) => (prev === 'starlink_any' ? null : 'starlink_any'))}
              >
                STARLINK · {starlinkSitesCount}
              </button>
            </>
          )}
        </div>

        {visibleSites.length === 0 && (
          <div className="sd-empty">
            {sites.length === 0
              ? 'No sites are linked to your account yet - contact your provider.'
              : 'No sites match this filter.'}
          </div>
        )}

        {visibleSites.length > 0 && (
          <div className="sd-fleet-table">
            {!isMobile && (
              <div className="sd-fleet-head-row">
                <div></div>
                <div>SITE</div>
                <div>STATE</div>
                <div>DEVICES</div>
                <div className="ft-num">ONLINE</div>
                <div>NEEDS ATTENTION</div>
                <div className="ft-num">ALERTS</div>
              </div>
            )}
            {visibleSites.map((site) => {
              const peplinks = site.devices.filter((d) => d.device_type === 'peplink');
              const starlinks = site.devices.filter((d) => d.device_type === 'starlink');
              const starlinks_roam = site.devices.filter((d) => d.device_type === 'starlink' && d.service === 'roam');
              const starlinks_maritime = site.devices.filter((d) => d.device_type === 'starlink' && d.service === 'managed');

              const allDevices = site.devices;
              const countedDevices = allDevices.filter((d) => !isPendingRoam(d));
              const offlineCount = countedDevices.filter((d) => d.online === false).length;

              let state = 'unknown';
              if (countedDevices.length > 0) {
                if (offlineCount === countedDevices.length) state = 'down';
                else if (offlineCount > 0) state = 'degraded';
                else state = 'online';
              }

              const siteDownSince = state === 'down' ? getSiteDownSince(allDevices) : null;
              const downDuration = formatDownDuration(siteDownSince);

              const stateBadge = allDevices.length === 0 ? (
                <span className="ft-badge ft-badge-unknown">NO DEVICES</span>
              ) : (
                <span className={'ft-badge ft-badge-' + (state === 'down' ? 'down' : state === 'degraded' ? 'degraded' : 'online')}>
                  {state === 'down' ? `DOWN${downDuration ? ` · ${downDuration}` : ''}` : state === 'degraded' ? 'DEGRADED' : 'ONLINE'}
                </span>
              );

              const stateColor =
                state === 'down' ? '#fff' :
                state === 'degraded' ? 'var(--color-accent)' :
                state === 'online' ? 'var(--color-secondary)' : 'rgba(255,255,255,.3)';

              // Same "needs attention" narrative rule as the admin page -
              // built only from signals this list actually carries, with
              // a plain offline count fallback rather than inventing a
              // cause when none of those specific signals explain it.
              const offlineStarlinkMaritimeCount = starlinks.filter((d) => d.online === false && d.service === 'managed').length;
              const offlineImportantClients = allDevices.reduce((s, d) => s + (d.offline_important_clients_count || 0), 0);
              const offlineImportantWan = allDevices.reduce((s, d) => s + (d.offline_important_wan_count || 0), 0);

              let attentionText;
              if (allDevices.length === 0) {
                attentionText = 'No devices assigned';
              } else if (state === 'down') {
                attentionText = `All devices unreachable${siteDownSince ? ` — last contact ${new Date(siteDownSince).toUTCString().slice(17, 22)} UTC` : ''}`;
              } else if (state === 'degraded') {
                const parts = [];
                if (offlineStarlinkMaritimeCount > 0) parts.push(`${offlineStarlinkMaritimeCount} Starlink Maritime line${offlineStarlinkMaritimeCount > 1 ? 's' : ''} offline`);
                if (offlineImportantClients > 0) parts.push(`${offlineImportantClients} important client${offlineImportantClients > 1 ? 's' : ''} offline`);
                if (offlineImportantWan > 0) parts.push(`${offlineImportantWan} important WAN${offlineImportantWan > 1 ? 's' : ''} offline`);
                attentionText = parts.length > 0 ? parts.join(' · ') : `${offlineCount} device${offlineCount > 1 ? 's' : ''} offline`;
              } else {
                attentionText = 'Nothing outstanding';
              }

              const alertCount = state === 'down'
                ? allDevices.length
                : (offlineStarlinkMaritimeCount > 0 ? 1 : 0) + (offlineImportantClients > 0 ? 1 : 0) + (offlineImportantWan > 0 ? 1 : 0);

              return (
                <React.Fragment key={site.site_id}>
                  {isMobile ? (
                    <div
                      className="sd-fleet-row-mobile"
                      style={{
                        borderLeft: `2px solid ${stateColor}`,
                        background:
                          state === 'down'
                            ? 'rgba(255,255,255,.035)'
                            : state === 'degraded' && expanded[site.site_id]
                            ? 'rgba(202,251,109,.05)'
                            : 'transparent',
                      }}
                      data-site-row={site.site_id}
                      data-tour={tourSite && site.site_id === tourSite.site_id ? 'sd-site' : undefined}
                      onClick={() => toggle(site.site_id)}
                    >
                      <div className="sd-fleet-card-top">
                        <span className={'sd-caret' + (expanded[site.site_id] ? ' open' : '')} style={{ color: expanded[site.site_id] ? stateColor : 'rgba(255,255,255,.4)' }}>
                          {expanded[site.site_id] ? '▾' : '▸'}
                        </span>
                        <span className="sd-site-name sd-site-name-tight">{site.site_name}</span>
                        <span className={'sd-type-badge ' + site.site_type}>
                          {SITE_TYPE_LABEL[site.site_type] || site.site_type}
                        </span>
                        <span className="sd-fleet-card-state">{stateBadge}</span>
                      </div>
                      <div className="sd-fleet-card-chips">
                        {peplinks.length > 0 && <span className="sd-fleet-chip">{peplinks.length} PEPLINK</span>}
                        {starlinks_maritime.length > 0 && <span className="sd-fleet-chip">{starlinks_maritime.length} MARITIME</span>}
                        {starlinks_roam.length > 0 && <span className="sd-fleet-chip">{starlinks_roam.length} ROAM</span>}
                        <span className="sd-fleet-online-frac" style={{ color: stateColor }}>
                          {allDevices.length - offlineCount}/{allDevices.length} online
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="sd-fleet-grid-row"
                      style={{ borderLeft: `2px solid ${stateColor}`, background: expanded[site.site_id] ? (state === 'down' ? 'rgba(255,255,255,.035)' : state === 'degraded' ? 'rgba(202,251,109,.05)' : 'transparent') : 'transparent' }}
                      data-site-row={site.site_id}
                      data-tour={tourSite && site.site_id === tourSite.site_id ? 'sd-site' : undefined}
                      onClick={() => toggle(site.site_id)}
                    >
                      <div className={'sd-caret' + (expanded[site.site_id] ? ' open' : '')} style={{ color: expanded[site.site_id] ? stateColor : 'rgba(255,255,255,.4)' }}>
                        {expanded[site.site_id] ? '▾' : '▸'}
                      </div>
                      <div className="sd-fleet-site-cell">
                        <div className="sd-fleet-site-name-row">
                          <span className="sd-site-name">{site.site_name}</span>
                          <span className={'sd-type-badge ' + site.site_type}>
                            {SITE_TYPE_LABEL[site.site_type] || site.site_type}
                          </span>
                        </div>
                      </div>
                      <div>{stateBadge}</div>
                      <div className="sd-fleet-devices-cell">
                        {peplinks.length > 0 && <span className="sd-devices-chip">{peplinks.length} PEPLINK</span>}
                        {starlinks_maritime.length > 0 && <span className="sd-devices-chip">{starlinks_maritime.length} MARITIME</span>}
                        {starlinks_roam.length > 0 && <span className="sd-devices-chip">{starlinks_roam.length} ROAM</span>}
                      </div>
                      <div className="ft-num" style={{ color: stateColor }}>
                        {allDevices.length - offlineCount}/{allDevices.length}
                      </div>
                      <div className="sd-fleet-attention-cell" style={{ color: state === 'down' ? '#fff' : state === 'online' ? 'rgba(255,255,255,.45)' : 'rgba(255,255,255,.72)' }}>
                        {attentionText}
                      </div>
                      <div className="ft-num">
                        {allDevices.length === 0 ? (
                          <span style={{ color: 'rgba(255,255,255,.3)' }}>0</span>
                        ) : alertCount > 0 ? (
                          <span
                            className="sd-fleet-alert-chip"
                            style={
                              state === 'down'
                                ? { background: '#fff', color: 'var(--color-primary)' }
                                : { background: 'rgba(202,251,109,.18)', color: 'var(--color-accent)' }
                            }
                          >
                            {alertCount}
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,.3)' }}>0</span>
                        )}
                      </div>
                    </div>
                  )}

                  {expanded[site.site_id] && (
                    <div className="sd-fleet-expand-row">
                      <div className="sd-device-list">
                        {site.devices.length === 0 && (
                          <div className="sd-no-devices">No devices on this site yet</div>
                        )}
                        {!isMobile && site.devices.length > 0 && (
                          <div className="sd-device-grid-head">
                            <div>DEVICE</div>
                            <div>STATE</div>
                            <div>WANS</div>
                            <div>CLIENTS</div>
                            <div className="ft-num">24H DATA</div>
                            <div className="ft-num">UPTIME</div>
                          </div>
                        )}
                        {site.devices.map((d) => (
                          <React.Fragment key={d.device_type + d.device_key}>
                            <DeviceRow
                              device={d}
                              onClick={() => handleRowClick(d)}
                              onOpenMode={(mode) => handleStatClick(d, mode)}
                              isOpen={expandedDevice === d.device_key}
                              isMobile={isMobile}
                              tourId={tourDevice && site.site_id === tourSite?.site_id && d.device_key === tourDevice.device_key ? 'sd-device' : undefined}
                            />
                            {d.device_type === 'peplink' && expandedDevice === d.device_key && (
                              <LiveWanPanel
                                device={d}
                                initialMode={expandedMode}
                                canEdit={canEdit}
                                scope="client"
                                onClose={() => setExpandedDevice(null)}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
